import { useAtomValue } from "jotai";
import { rightSidebarWidthAtom } from "@/atoms/uiAtoms";
import {
  CASE_HEADER,
  LIST_HEADER,
  STUDY_CELL,
  STUDY_EDGE_CELL,
  STUDY_HEAD_ROW,
  studyHeadCell,
} from "@/components/devicePanel/panelLayout";
import { STUDY_COLUMNS } from "@/components/devicePanel/PanelWorklist";

// Quiet placeholders while a device panel loads: no sentence, the real layout's
// shapes in a faint pulse that stops under prefers-reduced-motion. Each mirrors
// the geometry of what replaces it, so the swap does not shift the page.

/** One placeholder block; its size comes from `className`. */
const Block = ({ className }: { className: string }) => (
  <span aria-hidden className={`block rounded-xs bg-surface-2 ${className}`} />
);

const PULSE = "animate-pulse motion-reduce:animate-none";

// Chip widths per placeholder row, so the rows do not read as one repeated shape.
const ROW_CHIPS = [
  ["w-36", "w-24"],
  ["w-28"],
  ["w-40", "w-20", "w-24"],
  ["w-32"],
  ["w-24", "w-36"],
  ["w-44", "w-28"],
  ["w-20"],
];

// The columns every worklist shows; exam and age appear only with their data.
const ALWAYS_SHOWN = STUDY_COLUMNS.filter((column) => !column.shown);

/** The study worklist loading: its header, then the table's own heading row
 * and rows in its cells: case and finding chips. */
export function PanelListSkeleton() {
  return (
    <div role="status" aria-label="Loading panel" aria-busy className="flex h-full flex-col bg-bg">
      <div className={`${LIST_HEADER} ${PULSE}`}>
        <Block className="h-4 w-32" />
        <Block className="h-4 w-16" />
        <Block className="h-3 w-24" />
      </div>
      <table className={`w-full border-collapse ${PULSE}`}>
        <thead>
          <tr className={STUDY_HEAD_ROW}>
            {ALWAYS_SHOWN.map((column, index) => (
              <th key={column.heading} className={studyHeadCell(index)}>
                {column.heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROW_CHIPS.map((chips, index) => (
            <tr key={index} data-skeleton-row className="border-b border-border">
              <td className={STUDY_EDGE_CELL}>
                <Block className="h-4 w-12" />
              </td>
              <td className={`${STUDY_CELL} flex gap-1.5`}>
                {chips.map((width, chip) => (
                  <Block key={chip} className={`h-5 ${width}`} />
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The rail's triage rows loading, in the real row grid. */
export function PanelRailSkeleton({ width }: { width?: number }) {
  return (
    <div
      role="status"
      aria-label="Loading"
      aria-busy
      style={width === undefined ? undefined : { width }}
      className={`h-full shrink-0 bg-bg-2 ${PULSE}`}
    >
      <div className="border-b border-border px-3 pb-2 pt-2.5">
        <Block className="h-5 w-16" />
      </div>
      {ROW_CHIPS.slice(0, 5).map(([width], index) => (
        <div
          key={index}
          className="grid grid-cols-[74px_minmax(0,1fr)] items-center gap-2.5 border-t border-border px-3 py-2"
        >
          <Block className="h-4 w-14" />
          <Block className={`h-5 ${width}`} />
        </div>
      ))}
    </div>
  );
}

/** A study loading: its header bar, then the viewer area and the rail at the
 * viewer's own rail width. */
export function PanelCaseSkeleton() {
  const railWidth = useAtomValue(rightSidebarWidthAtom);
  return (
    <div role="status" aria-label="Loading study" aria-busy className="flex h-full flex-col">
      <div className={`${CASE_HEADER} ${PULSE}`}>
        <Block className="h-4 w-24" />
        <Block className="h-4 w-12" />
        <Block className="h-4 w-16" />
        <Block className="h-4 w-16" />
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="flex-1 bg-black" />
        <PanelRailSkeleton width={railWidth} />
      </div>
    </div>
  );
}
